import React, { useEffect, useState } from "react";
import Icon from "../utils/icons";
import { money, discountPercent, PLACEHOLDER_IMG, sizedImage } from "../utils/formatters";
import { colorsOf, optionLabelOf, personalOf, pickComplete, pricedAt, shownPrice, sizesOf } from "../utils/variants";

// Body measurements in inches for the usual sizes, shown in the size guide. Sizes the owner made up (32, 6XL, ...)
// are listed without measurements.
const SIZE_CHART = {
    XS: [32, 26, 34], S: [34, 28, 36], M: [36, 30, 38], L: [38, 32, 40], XL: [40, 34, 42], XXL: [42, 36, 44],
    "3XL": [44, 38, 46], "4XL": [46, 40, 48], "5XL": [48, 42, 50],
};
const KIDS_CHART = { "2-3Y": [37, 21], "4-5Y": [43, 23], "6-7Y": [49, 25], "8-9Y": [53, 27], "10-11Y": [57, 29] };

export function SizeGuide({ sizes, onClose }) {
    const kids = sizes.some((s) => KIDS_CHART[s]);
    return (
        <div className="sizeGuideBackdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="sizeGuide" role="dialog" aria-modal="true" aria-label="Size guide">
                <div className="sizeGuideHead">
                    <h3>Size guide</h3>
                    <button type="button" onClick={onClose} aria-label="Close size guide"><Icon.XLg /></button>
                </div>
                <p>{kids ? "Height and chest in inches. Between two sizes? Pick the bigger one, kids grow fast." : "Body measurements in inches. Between two sizes? Pick the bigger one for a relaxed fit."}</p>
                <table>
                    <thead>{kids ? <tr><th>Age</th><th>Height</th><th>Chest</th></tr> : <tr><th>Size</th><th>Bust</th><th>Waist</th><th>Hip</th></tr>}</thead>
                    <tbody>
                        {sizes.map((s) => {
                            const row = kids ? KIDS_CHART[s] : SIZE_CHART[s.toUpperCase()];
                            return <tr key={s}><td><b>{s}</b></td>{row ? row.map((v, i) => <td key={i}>{v}"</td>) : <td colSpan={kids ? 2 : 3}>As described</td>}</tr>;
                        })}
                    </tbody>
                </table>
                <small>📏 Not sure? Message us on WhatsApp with your height and usual size, and we will help you pick.</small>
            </div>
        </div>
    );
}

// Size chips and colour swatches. A size or colour the shopper has not chosen yet is shown as missing when `nudge`
// changes (they pressed Add without choosing), with a little shake.
export function VariantPicker({ product, pick, setPick, nudge = 0 }) {
    const [guide, setGuide] = useState(false);
    const sizes = sizesOf(product);
    const colors = colorsOf(product);
    // Gift stores name their options ("Mug size"); the size guide is for clothes only
    const label = optionLabelOf(product);
    const clothesSizes = label === "Size";
    const missingSize = nudge > 0 && sizes.length > 0 && !sizes.includes(pick.size);
    const missingColor = nudge > 0 && colors.length > 0 && !colors.some((c) => c.name === pick.color);
    // Sizes that cost different amounts show their price on the chip
    const pricesVary = shownPrice(product).from;

    return (
        <div className="variantPicker">
            {colors.length > 0 && (
                <div className={`variantGroup ${missingColor ? "isMissing" : ""}`} key={missingColor ? `c${nudge}` : "c"}>
                    <div className="variantLabel"><b>Colour</b><span>{pick.color || (missingColor ? "Please choose a colour" : "Choose one")}</span></div>
                    <div className="swatches" role="radiogroup" aria-label="Colour">
                        {colors.map((c) => (
                            <button type="button" key={c.name} role="radio" aria-checked={pick.color === c.name} aria-label={c.name} title={c.name}
                                className={`swatch ${pick.color === c.name ? "on" : ""}`} onClick={() => setPick({ ...pick, color: c.name })}>
                                <i style={{ background: c.hex || "#ddd" }} />
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {sizes.length > 0 && (
                <div className={`variantGroup ${missingSize ? "isMissing" : ""}`} key={missingSize ? `s${nudge}` : "s"}>
                    <div className="variantLabel">
                        <b>{label}</b><span>{pick.size || (missingSize ? `Please choose a ${label.toLowerCase()}` : "Choose one")}</span>
                        {clothesSizes && <button type="button" className="sizeGuideLink" onClick={() => setGuide(true)}><Icon.Rulers /> Size guide</button>}
                    </div>
                    <div className="sizeChips" role="radiogroup" aria-label={label}>
                        {sizes.map((s) => (
                            <button type="button" key={s} role="radio" aria-checked={pick.size === s} className={`sizeChip ${pick.size === s ? "on" : ""}`} onClick={() => setPick({ ...pick, size: s })}>{s}{pricesVary && <small className="sizeChipPrice">{money(pricedAt(product, s).price)}</small>}</button>
                        ))}
                    </div>
                </div>
            )}
            {guide && <SizeGuide sizes={sizes} onClose={() => setGuide(false)} />}
        </div>
    );
}

// The bottom sheet that opens when a sized product is added from a card ("Add to bag"): pick, then add.
// Opened by askForPick (utils/variants.js) through the "gn:pick" event.
export default function VariantSheet({ add, onSelect }) {
    const [product, setProduct] = useState(null);
    const [pick, setPick] = useState({});
    const [nudge, setNudge] = useState(0);

    useEffect(() => {
        const open = (event) => {
            const p = event.detail;
            // A gift to personalise needs its page: the message box and photo upload are there
            if (personalOf(p) && onSelect) {
                onSelect(p);
                return;
            }
            const colors = colorsOf(p);
            // One colour or one size needs no choosing
            setPick({ color: colors.length === 1 ? colors[0].name : undefined, size: sizesOf(p).length === 1 ? sizesOf(p)[0] : undefined });
            setNudge(0);
            setProduct(p);
        };
        window.addEventListener("gn:pick", open);
        return () => window.removeEventListener("gn:pick", open);
    }, [onSelect]);

    useEffect(() => {
        if (!product) return undefined;
        const onKey = (event) => event.key === "Escape" && setProduct(null);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [product]);

    if (!product) return null;
    const shown = shownPrice(product, pick.size); // the picked size's price ("From" the cheapest before a pick)
    const discount = discountPercent(shown);
    const confirm = () => {
        if (!pickComplete(product, pick)) return setNudge((n) => n + 1);
        add(product, pick);
        setProduct(null);
        return undefined;
    };

    return (
        <div className="variantSheetBackdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && setProduct(null)}>
            <div className="variantSheet" role="dialog" aria-modal="true" aria-label={`Choose options for ${product.name}`}>
                <span className="variantSheetGrip" aria-hidden="true" />
                <button type="button" className="variantSheetClose" onClick={() => setProduct(null)} aria-label="Close"><Icon.XLg /></button>
                <div className="variantSheetTop">
                    <img src={product.img ? sizedImage(product.img, 300) : PLACEHOLDER_IMG} alt="" />
                    <div>
                        <h3>{product.name}</h3>
                        <div className="variantSheetPrice">
                            <strong>{shown.from && "From "}{money(shown.price)}</strong>
                            {discount > 0 && <><del>{money(shown.originalPrice)}</del><span>{discount}% off</span></>}
                        </div>
                        {onSelect && <button type="button" className="variantSheetMore" onClick={() => { setProduct(null); onSelect(product); }}>View details <Icon.ArrowRight /></button>}
                    </div>
                </div>
                <VariantPicker product={product} pick={pick} setPick={setPick} nudge={nudge} />
                <button type="button" className="variantSheetAdd" onClick={confirm}><Icon.Bag /> Add to bag · {money(shown.price)}</button>
            </div>
        </div>
    );
}
